/*
 * Copyright (c) 2022. World Wide Generation Ltd
 */

import React, { useCallback, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { Button } from 'reactstrap';
import { useAppDispatch, useAppSelector } from '../../reducers';
import { generateUrl } from '../../routes/util';
import G17Client from '../../services/G17Client';
import { addSiteAlert, SiteAlertColors } from '../../slice/siteAlertsSlice';
import { CustomReport } from '../../types/custom-report';
import { formatDate } from '../../utils/date';
import { DashboardSection, DashboardSectionTitle } from '../dashboard';
import Loader from '../loader';
import SimpleTooltip from '../simple-tooltip';
import Table, { TableColumn } from '../table/Table';
import { CustomReportRoutes } from './CustomReportsRoot';
import { isUserManager } from '../../selectors/user';
import IconButton from '../button/IconButton';
import './styles.scss';
import { UpgradeRequired } from '../../routes/upgrade-required';
import { FeaturePermissions } from '../../services/permissions/FeaturePermissions';

const getType = (type: string) => {
  switch (type) {
    case 'initiatives':
      return 'Subsidiary';
    case 'survey':
    case 'survey_aggregation':
      return 'Date range';
    case 'metrics':
    default:
      return 'Calculated';
  }
}

const columns: TableColumn[] = [
  {
    Header: 'Name of report',
    accessor: 'reportName',
    cellProps: {
      className: 'truncated-contaienr flex-grow-1'
    }
  },
  {
    Header: 'Type',
    accessor: 'type',
    Cell: (c) => getType(c.value)
  },
  {
    Header: 'Created',
    accessor: 'created',
    Cell: (c) => formatDate(c.value)
  },
  {
    Header: 'Last updated',
    accessor: 'lastUpdated',
    Cell: (c) => formatDate(c.value)
  },
  {
    id: 'buttons',
    Header: '',
    accessor: 'buttons',
    cellProps: {
      className: 'text-nowrap'
    },
    Cell: (c) => (
      <>
        <IconButton icon='fa-file-excel text-primary' onClick={c.value.handleDownload} className='ml-1' />
        {c.value.handleCopy ?
          <IconButton icon='fa-copy' onClick={c.value.handleCopy} className='ml-1' />
          : <></>
        }
        {c.value.handleEdit ?
          <IconButton icon='fa-pencil-alt' onClick={c.value.handleEdit} className='ml-1' />
          : <></>
        }
        {c.value.handleDelete ?
          <IconButton icon='fa-trash text-ThemeDangerMedium' onClick={c.value.handleDelete} className='ml-1' />
          : <></>
        }
      </>
    ),
  },
];

const CustomReportsList = () => {

  const [customReports, setCustomReports] = useState<CustomReport[] | undefined>(undefined);
  const initiativeState = useAppSelector((state) => state.initiative);

  const isManager = useAppSelector(isUserManager);
  const canAccessCombinedReport = useAppSelector(FeaturePermissions.canAccessCombinedReport);

  const history = useHistory();
  const dispatch = useAppDispatch();

  const handleReload = useCallback(() => {
    if (initiativeState.loaded) {
      G17Client.getCustomReports(initiativeState.id)
        .then((reports) => setCustomReports(reports))
        .catch(() => {
          dispatch(addSiteAlert({
            content: 'Failed to fetch the list of custom reports. Please try again later',
            color: SiteAlertColors.Danger,
          }))
        })
    }
  }, [initiativeState, dispatch]);

  React.useEffect(() => {
    handleReload();
  }, [initiativeState.loaded, handleReload]);

  if (!initiativeState.loaded) {
    return <Loader />;
  }

  const initiativeId = initiativeState.id;

  const handleCopy = (reportId: string) => {
    G17Client.cloneCustomReport(initiativeState.id, reportId)
      .then(() => handleReload())
      .catch(() => {
        dispatch(addSiteAlert({
          content: 'Failed to fetch the list of custom reports. Please try again later',
          color: SiteAlertColors.Danger,
        }))
      })
  }

  const handleDelete = async (reportId: string) => {
    G17Client.deleteCustomReport(initiativeId, reportId)
      .then(() => handleReload())
      .catch(() => {
        dispatch(addSiteAlert({
          content: 'Unable to delete custom report. Please try again.',
          color: SiteAlertColors.Danger,
        }));
      });
  }

  const handleEdit = (reportId: string) => {
    history.push(generateUrl(CustomReportRoutes.MANAGE_REPORT, { initiativeId, reportId }));
  }

  const handleDownload = (reportId: string) => {
    return G17Client.downloadCustomReport(initiativeState.id, reportId).catch((e) => console.log(e));
  };

  const handleGoToReport = (reportId: string) => {
    history.push(generateUrl(CustomReportRoutes.MANAGE_REPORT, { initiativeId, reportId }));
  }

  const handleGoToCreateReport = () => {
    history.push(generateUrl(CustomReportRoutes.CREATE_REPORT, { initiativeId }));
  }

  if (!canAccessCombinedReport) {
    return <UpgradeRequired />
  }

  return (
    <>
      <DashboardSectionTitle title='Manage custom reports' />
      <DashboardSection className='custom-reports-list' >
        <div>
          {!customReports ? <Loader relative={true} /> :
            <Table columns={columns} responsive={true} data={
              customReports.map((report) => ({
                ...report,
                reportName: (
                  <div className='text-truncate'><SimpleTooltip text={<>{report.name}<br />{report.description}</>}>
                    <Button color='link' onClick={() => handleGoToReport(report._id)}>{report.name}</Button>
                  </SimpleTooltip></div>
                ),
                buttons: {
                  handleCopy: isManager ? () => handleCopy(report._id) : null,
                  handleEdit: isManager ? () => handleEdit(report._id) : null,
                  handleDelete: isManager ? () => handleDelete(report._id) : null,
                  handleDownload: () => handleDownload(report._id)
                }
              }))
            }
              noData='Use the form below to create a new custom report' />
          }
        </div>
        <div className='mt-5 text-center'>
          <Button color='primary' outline={Boolean(customReports?.length)} onClick={handleGoToCreateReport}>Create a new report</Button>
        </div>
      </DashboardSection>
    </>
  );
}

export default CustomReportsList;
